import { SIDEBAR_LIST } from '@/constants/sidebar';
import { SideBarItem } from './SideBarItem';
import sidebarLogo from '@/assets/images/sidebar-logo.png';
import { SubSideBarItem } from './SubSideBarItem';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { useLocation } from 'react-router';

interface SideBarProps {
  onClose?: () => void;
}

function SideBar({ onClose }: SideBarProps) {
  const location = useLocation();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  // Auto-expand parents when a child route is active
  useEffect(() => {
    const updates: Record<string, boolean> = {};
    SIDEBAR_LIST.forEach(item => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(child =>
          location.pathname.startsWith(child.target)
        );
        if (hasActiveChild) {
          updates[item.target] = true;
        }
      }
    });
    if (Object.keys(updates).length > 0) {
      setOpenItems(prev => ({ ...prev, ...updates }));
    }
  }, [location.pathname]);

  const toggleItem = (itemId: string) => {
    setOpenItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  return (
    <aside className="flex h-screen w-[260px] flex-col border-r border-coal-300 bg-white">
      {/* Logo & close button */}
      <div className="flex h-14 items-center justify-between px-5">
        <img src={sidebarLogo} alt="Kotobekia Logo" className="h-7" />
        <button
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-md text-coal-400 transition-colors hover:bg-coal-100 hover:text-coal-600 lg:hidden"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Menu label */}
      <div className="px-5 pb-2 pt-4">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-coal-400">
          Menu
        </span>
      </div>

      {/* Menu */}
      <div className="flex-1 overflow-y-auto px-3 py-1">
        <ul className="space-y-0.5">
          {SIDEBAR_LIST.map(item => {
            const isOpen = openItems[item.target] || false;
            const hasChildren = item.children && item.children.length > 0;

            return (
              <div key={item.target}>
                <SideBarItem
                  item={item}
                  onClick={hasChildren ? () => toggleItem(item.target) : undefined}
                  isDropdownOpen={isOpen}
                />
                {hasChildren && (
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-300 ease-in-out',
                      isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    )}
                  >
                    <ul className="ml-3 space-y-0.5 border-l border-coal-200 pl-3 pt-1">
                      {item.children?.map(child => (
                        <SubSideBarItem key={child.target} item={child} />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}

export default SideBar;
